import { io, Socket } from "socket.io-client";
import { config } from "@/lib/config";

interface MessageData {
  conversationId: string;
  receiverId: string;
  receiverType: "customer" | "agent";
  content: {
    text?: string;
    type: "text" | "image" | "video" | "document" | "file";
    mediaUrl?: string;
    fileName?: string;
    fileSize?: number;
    mimeType?: string;
  };
  replyTo?: string;
}

class SocketService {
  private socket: Socket | null = null;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private eventHandlers: Map<string, Set<(data: unknown) => void>> = new Map();

  constructor() {
    // Initialize socket with token from storage
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      this.token = storedToken;
    }
  }

  // Connect to socket server
  public connect(token: string): void {
    // Prevent multiple connections - check both connected AND socket existence
    if (this.socket) {
      if (this.socket.connected) {
        return;
      }
      // Socket exists but not connected - reconnect
      this.socket.connect();
      return;
    }

    this.token = token;

    // Socket.IO connection configuration
    // Extract base URL and construct Socket.IO path with API prefix
    const url = new URL(config.api.baseUrl);
    const baseUrl = `${url.protocol}//${url.host}`; // http://localhost:8000
    const apiPrefix = url.pathname.replace(/\/$/, ""); // /alkinarebuild
    const socketPath = apiPrefix ? `${apiPrefix}/socket.io/` : "/socket.io/";

    this.socket = io(baseUrl, {
      path: socketPath, // /alkinarebuild/socket.io/ or /socket.io/
      auth: {
        token: this.token,
      },
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.setupEventListeners();
  }

  // Disconnect from socket server
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.eventHandlers.clear();
    }
  }

  // Check if connected
  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Setup default event listeners
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      this.reconnectAttempts = 0;
      this.emit("custom:connected", { socketId: this.socket?.id });
    });

    this.socket.on("disconnect", (reason: string) => {
      this.emit("custom:disconnected", { reason });
    });

    this.socket.on("connect_error", (error: Error) => {
      console.error("Socket connection error:", error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error("Max reconnection attempts reached");
        this.emit("custom:reconnect_failed", {});
      }
    });

    // Message events
    this.socket.on("message:sent", (data: unknown) =>
      this.emit("message:sent", data)
    );
    this.socket.on("message:received", (data: unknown) =>
      this.emit("message:received", data)
    );
    this.socket.on("message:new", (data: unknown) =>
      this.emit("message:new", data)
    );
    this.socket.on("message:read:receipt", (data: unknown) =>
      this.emit("message:read:receipt", data)
    );
    this.socket.on("message:error", (data: unknown) =>
      this.emit("message:error", data)
    );

    // Typing events
    this.socket.on("user:typing", (data: unknown) =>
      this.emit("user:typing", data)
    );
    this.socket.on("user:typing:stop", (data: unknown) =>
      this.emit("user:typing:stop", data)
    );

    // Presence events
    this.socket.on("presence:update", (data: unknown) =>
      this.emit("presence:update", data)
    );

    // WhatsApp events
    this.socket.on("whatsapp:qr", (data: unknown) =>
      this.emit("whatsapp:qr", data)
    );
    this.socket.on("whatsapp:authenticated", (data: unknown) =>
      this.emit("whatsapp:authenticated", data)
    );
    this.socket.on("whatsapp:ready", (data: unknown) =>
      this.emit("whatsapp:ready", data)
    );
    this.socket.on("whatsapp:disconnected", (data: unknown) =>
      this.emit("whatsapp:disconnected", data)
    );
    this.socket.on("whatsapp:auth_failure", (data: unknown) =>
      this.emit("whatsapp:auth_failure", data)
    );
    this.socket.on("whatsapp:error", (data: unknown) =>
      this.emit("whatsapp:error", data)
    );
  }

  // Send a message
  public sendMessage(data: MessageData): void {
    if (!this.socket?.connected) {
      console.error("Socket not connected");
      return;
    }

    this.socket.emit("message:send", data);
  }

  // Mark message as read
  public markMessageAsRead(messageId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit("message:read", { messageId });
  }

  // Start typing indicator
  public startTyping(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit("message:typing:start", { conversationId });
  }

  // Stop typing indicator
  public stopTyping(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit("message:typing:stop", { conversationId });
  }

  // Join a conversation room
  public joinConversation(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit("conversation:join", { conversationId });
  }

  // Leave a conversation room
  public leaveConversation(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit("conversation:leave", { conversationId });
  }

  // Update presence status
  public updatePresence(status: "online" | "away" | "busy"): void {
    if (!this.socket?.connected) return;
    this.socket.emit("presence:update", { status });
  }

  // Subscribe to an event
  public on<T = unknown>(event: string, handler: (data: T) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)?.add(handler as (data: unknown) => void);
  }

  // Unsubscribe from an event
  public off(event: string, handler?: (data: unknown) => void): void {
    if (!handler) {
      this.eventHandlers.delete(event);
      return;
    }

    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.eventHandlers.delete(event);
      }
    }
  }

  // Emit custom events to subscribers
  private emit(event: string, data: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }
}

// Singleton instance
let socketServiceInstance: SocketService | null = null;

export const getSocketService = (): SocketService => {
  if (!socketServiceInstance) {
    socketServiceInstance = new SocketService();
  }
  return socketServiceInstance;
};

// Export type for TypeScript
export type { SocketService };

export default getSocketService;
